import { FlexBoxView } from '@play-co/timestep-core/flexbox';
import { ImageScaleView, View, ViewOpts } from '@play-co/timestep-core/ui';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import { Feature } from 'src/replicant/analytics';
import sounds from 'src/lib/sounds';
import { clamp } from 'src/replicant/utils';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';

interface TeamNumberFieldOpts extends ViewOpts {
  buttonScale?: number;
  default?: number;
  min?: number;
  max?: number;
  increment?: number;
}

export default class TeamNumberField extends View {
  private readonly decreaseButton: ButtonScaleView;
  private readonly increaseButton: ButtonScaleView;
  private readonly label: LangBitmapFontTextView;
  private readonly min?: number;
  private readonly max?: number;

  public get value() {
    return this._value;
  }
  private set value(value: number) {
    // Value must be a multiple of the increment.
    value = Math.floor(value / this.increment) * this.increment;

    // Clamp value to min/max.
    value = clamp(value, this.min ?? -Infinity, this.max ?? Infinity);

    this._value = value;
    if (this.label.text !== this._value.toString()) {
      this.label.text = this._value.toString();
    }
    this.decreaseButton.updateOpts({
      canHandleEvents: !this.min || value >= this.min,
    });
    this.increaseButton.updateOpts({
      canHandleEvents: !this.max || value <= this.max,
    });
  }
  private _value = 0;

  private increment = 1;

  constructor(opts: TeamNumberFieldOpts) {
    const width = opts.width ?? 204;
    const height = opts.height ?? 49;

    // Base view
    super({ ...opts, width, height });

    this.increment = opts.increment ?? 1;
    if (opts.min != null) {
      this.min = Math.floor(opts.min / this.increment) * this.increment;
    }
    if (opts.max != null) {
      this.max = Math.floor(opts.max / this.increment) * this.increment;
    }

    // Background image
    new ImageScaleView({
      superview: this,
      width,
      height,
      image: 'assets/ui/shared/frames/quantity_textfield.png',
      scaleMethod: '3slice',
      sourceSlices: {
        horizontal: { left: 50, right: 50 },
      },
    });

    // Flex layout
    const layout = new FlexBoxView({
      superview: this,
      width,
      height,
      flex: {
        alignItems: 'center',
      },
    });

    const buttonScale = opts.buttonScale ?? 1;
    const buttonWidth = 57 * buttonScale;
    const buttonHeight = 55 * buttonScale;

    this.decreaseButton = new ButtonScaleView({
      superview: layout,
      image: 'assets/ui/shared/buttons/arrow.png',
      imagePressed: 'assets/ui/shared/buttons/arrow_pressed.png',
      imageDisabled: 'assets/ui/shared/buttons/arrow_disabled.png',
      centerAnchor: true,
      scale: 1.1,
      width: buttonWidth,
      height: buttonHeight,
      tracking: {
        feature: Feature.MATCH2.TEAM._,
        $subFeature: Feature.MATCH2.TEAM.TAB_CREATE_REQUIRED_LEVEL_DECREASE,
      },
      onClick: async () => {
        sounds.playSound('ok');

        // Wrap value around if min/max is set.
        if (this.min && this._value <= this.min) {
          this.value = this.max ?? this._value;
          return;
        }
        this.value = this._value - this.increment;
      },
    });

    this.label = new LangBitmapFontTextView({
      width: width - buttonWidth * 2,
      height,
      superview: layout,
      font: 'Body',
      align: 'center',
      verticalAlign: 'center',
      color: '#FFFFFF',
      size: 36,
      localeText: () => 'XXX',
      offsetX: -2,
    });

    this.increaseButton = new ButtonScaleView({
      superview: layout,
      image: 'assets/ui/shared/buttons/arrow.png',
      imagePressed: 'assets/ui/shared/buttons/arrow_pressed.png',
      imageDisabled: 'assets/ui/shared/buttons/arrow_disabled.png',
      centerAnchor: true,
      scale: 1.1,
      flipX: true,
      width: buttonWidth,
      height: buttonHeight,
      tracking: {
        feature: Feature.MATCH2.TEAM._,
        $subFeature: Feature.MATCH2.TEAM.TAB_CREATE_REQUIRED_LEVEL_INCREASE,
      },
      onClick: async () => {
        sounds.playSound('ok');

        // Wrap value around if min/max is set.
        if (this.max && this._value >= this.max) {
          this.value = this.min ?? this._value;
          return;
        }

        this.value = this._value + this.increment;
      },
    });

    this.value = opts.default ?? 0;
  }
}
